import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Settings2, Upload, Check, Pencil, Activity, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { money, outletNames } from "../lib/format";
import { useOutlets } from "../lib/useOutlets";
import { PageIntro, Modal, Field, SelectField, Badge, Pagination } from "../components/UI";
import { ItemImportDialog } from "../components/ItemImportDialog";
import { usePagination } from "../lib/usePagination";
import { useAuth } from "../context/AuthContext";

export default function InventoryPage({ outlet }) {
  const { user } = useAuth();
  const outletsList = useOutlets();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [outlets, setOutlets] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [coa, setCoa] = useState([]);
  const [modal, setModal] = useState(null); // "add" | "edit" | "upload" | null
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [movements, setMovements] = useState(null); // { item, rows, totals } | null
  const [movLoading, setMovLoading] = useState(false);

  const openMovements = async (item) => {
    setMovements({ item: { name: item.name, sku: item.sku }, rows: [], totals: null });
    setMovLoading(true);
    try {
      const { data } = await api.get(`/items/${item.id}/movements`);
      setMovements(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setMovements(null);
    } finally {
      setMovLoading(false);
    }
  };

  const canEdit = ["admin", "purchasing", "warehouse"].includes(user?.role);
  const pager = usePagination(items, 100, `${outlet}|${search}`);

  const load = () => {
    const params = {};
    if (outlet && outlet !== "all") params.outlet = outlet;
    if (search) params.search = search;
    api.get("/items", { params }).then((r) => setItems(r.data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet, search]);
  useEffect(() => {
    api.get("/outlets").then((r) => setOutlets(r.data));
    api.get("/uoms").then((r) => setUoms((r.data || []).filter((u) => u.active)));
    api.get("/coa", { params: { active: true } }).then((r) => setCoa(r.data || [])).catch(() => {});
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(defaultForm());
    setModal("add");
  };

  const openEdit = (i) => {
    setEditingId(i.id);
    setForm({
      sku: i.sku || "",
      name: i.name || "",
      category: i.category || "Protein",
      unit: i.unit || "PCS",
      stock: String(i.stock ?? 0),
      min_stock: String(i.min_stock ?? 0),
      cost: String(i.cost ?? 0),
      outlet_code: i.outlet_code || "kitchen",
      supplier: i.supplier || "",
      notes: i.notes || "",
      purchase_uom: i.purchase_uom || "",
      issue_uom: i.issue_uom || "",
      uom_conversions: (i.uom_conversions || []).map((c) => ({
        from_uom: c.from_uom || "",
        factor: String(c.factor ?? ""),
      })),
      is_direct: i.is_direct !== false,
      account_code: i.account_code || "",
      rm_stock_code: i.rm_stock_code || "",
    });
    setModal("edit");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Item name is required");
    if (!form.sku || !form.sku.trim()) return toast.error("SKU must be entered manually");
    const conversions = (form.uom_conversions || [])
      .map((c) => ({
        from_uom: (c.from_uom || "").trim().toUpperCase(),
        factor: Number(c.factor || 0),
      }))
      .filter((c) => c.from_uom && c.factor > 0);
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/items/${editingId}`, {
          name: form.name,
          category: form.category,
          unit: form.unit,
          cost: Number(form.cost || 0),
          min_stock: Number(form.min_stock || 0),
          supplier: form.supplier,
          outlet_code: form.outlet_code,
          notes: form.notes || "",
          purchase_uom: form.purchase_uom || "",
          issue_uom: form.issue_uom || "",
          uom_conversions: conversions,
          is_direct: !!form.is_direct,
          account_code: form.account_code || null,
          rm_stock_code: form.rm_stock_code || "",
        });
        toast.success("Item updated. New price/UOM will apply to future transactions only.");
      } else {
        await api.post("/items", {
          ...form,
          sku: form.sku.trim(),
          cost: Number(form.cost || 0),
          min_stock: Number(form.min_stock || 0),
          stock: Number(form.stock || 0),
          uom_conversions: conversions,
        });
        toast.success("Item added successfully");
      }
      setModal(null);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageIntro
        eyebrow="Master data · stock & costing"
        title="Items"
        subtitle="Central catalog for all hotel and F&B needs."
        testid="inventory-title"
        action={
          <div className="action-cluster">
            <button
              data-testid="inventory-upload-button"
              className="secondary-button"
              onClick={() => setModal("upload")}
            >
              <Upload size={16} /> Import / Export
            </button>
            <button
              data-testid="add-item-button"
              className="primary-button"
              onClick={openAdd}
            >
              <Plus size={17} /> Add item
            </button>
          </div>
        }
      />
      <section className="panel">
        <div className="toolbar">
          <div className="search-box">
            <Search size={17} />
            <input
              data-testid="inventory-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by item name or SKU code..."
            />
          </div>
          <button className="secondary-button">
            <Settings2 size={16} /> Filter
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Category</th>
                <th>Outlet</th>
                <th>Stock</th>
                <th>COGS / unit</th>
                <th>Account</th>
                <th>RM Code</th>
                <th>Valuation</th>
                <th>Supplier</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: 40 }}>
                    No items yet. Click "Add item" or "Upload CSV".
                  </td>
                </tr>
              )}
              {pager.pageItems.map((i) => (
                <tr key={i.id} data-testid={`inventory-row-${i.id}`}>
                  <td>
                    <strong data-testid={`inventory-name-${i.id}`}>{i.name}</strong>
                    <small><Badge tone="neutral">{i.sku}</Badge></small>
                  </td>
                  <td>
                    <Badge>{i.category}</Badge>
                  </td>
                  <td>{outletNames[i.outlet_code] || i.outlet_code}</td>
                  <td className={i.stock <= i.min_stock ? "danger-text" : ""}>
                    <strong>
                      {i.stock} {i.unit}
                    </strong>
                    {i.stock <= i.min_stock && <small>Low stock</small>}
                  </td>
                  <td>{money(i.cost)}</td>
                  <td data-testid={`inventory-account-${i.id}`}>
                    {i.account_code ? <Badge tone="blue">{i.account_code}</Badge> : <small style={{ color: "var(--amber)" }}>Missing</small>}
                  </td>
                  <td data-testid={`inventory-rm-code-${i.id}`}>
                    {i.rm_stock_code ? <Badge tone="neutral">{i.rm_stock_code}</Badge> : <small>—</small>}
                  </td>
                  <td>
                    <strong>{money(i.stock * i.cost)}</strong>
                  </td>
                  <td>{i.supplier || "-"}</td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      data-testid={`inventory-movements-${i.id}`}
                      className="small-button"
                      onClick={() => openMovements(i)}
                    >
                      <Activity size={12} /> Movements
                    </button>
                    {canEdit && (
                      <button
                        data-testid={`inventory-edit-${i.id}`}
                        className="small-button"
                        style={{ marginLeft: 6 }}
                        onClick={() => openEdit(i)}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onChange={pager.setPage} testid="items-pagination" />
      </section>

      {(modal === "add" || modal === "edit") && (
        <Modal
          title={modal === "edit" ? "Edit item master" : "Add item master"}
          onClose={() => { setModal(null); setEditingId(null); }}
        >
          <form className="form-grid" onSubmit={save}>
            <Field label="Item name" testid="item-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g., Fresh ribeye 200g" />
            <Field label={modal === "edit" ? "SKU code (cannot be changed)" : "SKU code (required)"} testid="item-sku-input" value={form.sku || ""} onChange={(v) => setForm({ ...form, sku: v })} placeholder="e.g., PRT-001 / BEV-042" required disabled={modal === "edit"} />
            <Field label="Main supplier" testid="item-supplier-input" value={form.supplier} onChange={(v) => setForm({ ...form, supplier: v })} placeholder="Supplier name" />
            <SelectField label="Category" testid="item-category-select" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={["Protein", "Dry goods", "Beverage", "Dairy", "Amenities", "Vegetable", "Other"]} />
            <SelectField
              label="Outlet"
              testid="item-outlet-select"
              value={form.outlet_code}
              onChange={(v) => setForm({ ...form, outlet_code: v })}
              options={outletsList.map((o) => ({ value: o.code, label: o.name }))}
            />
            <SelectField
              label="Unit of measure (UOM)"
              testid="item-unit-select"
              value={form.unit}
              onChange={(v) => setForm({ ...form, unit: v })}
              options={uoms.length > 0 ? uoms.map((u) => ({ value: u.code, label: `${u.code} — ${u.name}` })) : ["PCS"]}
            />
            {modal !== "edit" && (
              <Field label="Initial stock" testid="item-stock-input" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} />
            )}
            <Field label="Minimum stock" testid="item-min-input" type="number" value={form.min_stock} onChange={(v) => setForm({ ...form, min_stock: v })} />
            <Field label="Price / COGS per unit (IDR)" testid="item-cost-input" type="number" value={form.cost} onChange={(v) => setForm({ ...form, cost: v })} />
            <label className="field">
              <span>Account code (COA — for Xero)</span>
              <select
                data-testid="item-account-select"
                value={form.account_code || ""}
                onChange={(e) => setForm({ ...form, account_code: e.target.value })}
              >
                <option value="">— No account —</option>
                {coa.map((a) => (
                  <option key={a.id} value={a.code}>{a.code} - {a.name}</option>
                ))}
              </select>
            </label>
            <Field label="RM mapping stock code" testid="item-rm-stock-code-input" value={form.rm_stock_code || ""} onChange={(v) => setForm({ ...form, rm_stock_code: v })} placeholder="External RM system stock code" />
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Direct outlet receiving</span>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  data-testid="item-is-direct"
                  checked={!!form.is_direct}
                  onChange={(e) => setForm({ ...form, is_direct: e.target.checked })}
                />
                <small>Allow this item to be received directly at an outlet (bypasses Main Warehouse; consumed on receipt)</small>
              </label>
            </label>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Item description / notes</span>
              <textarea
                data-testid="item-notes-input"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Preferred brand, storage instructions, packaging spec..."
                rows={3}
              />
            </label>

            {/* --- Multi-UOM & conversion factors (optional) --- */}
            <div style={{ gridColumn: "1/-1", padding: 12, background: "#f6f8f6", borderRadius: 6, border: "1px dashed var(--line)" }}>
              <p className="eyebrow" style={{ margin: 0 }}>UOM conversions (optional)</p>
              <p className="form-hint" style={{ margin: "4px 0 10px" }}>
                Stock UOM is <b>{form.unit || "—"}</b>. Add alternate UOMs used in POs / issues
                (e.g. <code>CARTON → 24 {form.unit || "unit"}</code>). Every PR / PO / GRN qty in a
                different UOM will auto-convert to <b>{form.unit || "—"}</b> for stock valuation.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label className="field">
                  <span>Purchasing UOM</span>
                  <select
                    data-testid="item-purchase-uom"
                    value={form.purchase_uom || ""}
                    onChange={(e) => setForm({ ...form, purchase_uom: e.target.value })}
                  >
                    <option value="">Same as stock ({form.unit || "—"})</option>
                    {uoms.map((u) => <option key={u.code} value={u.code}>{u.code} — {u.name}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span>Issue UOM</span>
                  <select
                    data-testid="item-issue-uom"
                    value={form.issue_uom || ""}
                    onChange={(e) => setForm({ ...form, issue_uom: e.target.value })}
                  >
                    <option value="">Same as stock ({form.unit || "—"})</option>
                    {uoms.map((u) => <option key={u.code} value={u.code}>{u.code} — {u.name}</option>)}
                  </select>
                </label>
              </div>
              <table style={{ marginTop: 10, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", fontSize: 11 }}>Alternate UOM</th>
                    <th style={{ textAlign: "left", fontSize: 11 }}>= how many {form.unit || "stock UOM"}?</th>
                    <th style={{ width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.uom_conversions || []).map((c, idx) => (
                    <tr key={idx} data-testid={`uom-conv-row-${idx}`}>
                      <td>
                        <select
                          data-testid={`uom-conv-from-${idx}`}
                          value={c.from_uom}
                          onChange={(e) => {
                            const next = [...form.uom_conversions];
                            next[idx] = { ...next[idx], from_uom: e.target.value };
                            setForm({ ...form, uom_conversions: next });
                          }}
                          style={{ width: "100%", padding: 6 }}
                        >
                          <option value="">— select —</option>
                          {uoms.filter((u) => u.code !== form.unit).map((u) => (
                            <option key={u.code} value={u.code}>{u.code}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          data-testid={`uom-conv-factor-${idx}`}
                          type="number"
                          step="0.0001"
                          min="0"
                          value={c.factor}
                          onChange={(e) => {
                            const next = [...form.uom_conversions];
                            next[idx] = { ...next[idx], factor: e.target.value };
                            setForm({ ...form, uom_conversions: next });
                          }}
                          placeholder="e.g. 24"
                          style={{ width: "100%", padding: 6, border: "1px solid var(--line)", borderRadius: 5 }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          data-testid={`uom-conv-remove-${idx}`}
                          className="icon-button"
                          onClick={() => setForm({ ...form, uom_conversions: form.uom_conversions.filter((_, i) => i !== idx) })}
                          title="Remove"
                        >
                          <Pencil size={12} style={{ transform: "rotate(45deg)" }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                data-testid="uom-conv-add"
                className="chip small"
                style={{ marginTop: 8 }}
                onClick={() => setForm({ ...form, uom_conversions: [...(form.uom_conversions || []), { from_uom: "", factor: "" }] })}
              >
                <Plus size={12} /> Add conversion
              </button>
            </div>
            {modal === "edit" && (
              <p className="form-hint" style={{ gridColumn: "1/-1" }}>
                Note: updates to price or UOM apply to <b>new transactions only</b> — existing PO, GRN, and stock-out records keep their original values.
              </p>
            )}
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => { setModal(null); setEditingId(null); }}>Cancel</button>
              <button data-testid="item-save-button" className="primary-button" type="submit" disabled={saving}>
                <Check size={16} /> {saving ? "Saving..." : editingId ? "Update item" : "Save item"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "upload" && (
        <ItemImportDialog
          onClose={() => setModal(null)}
          onSuccess={load}
        />
      )}

      {movements && (
        <Modal
          title={`Stock movement — ${movements.item?.name || ""}`}
          eyebrow={movements.item?.sku ? `SKU ${movements.item.sku}` : "Kartu stok"}
          onClose={() => setMovements(null)}
          maxWidth={820}
        >
          <div data-testid="item-movements-modal">
            {movLoading ? (
              <p style={{ padding: 20, textAlign: "center" }}>Loading...</p>
            ) : (
              <>
                {movements.totals && (
                  <div className="result-summary" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                    <span className="chip small">Current stock: <b>{movements.item.stock} {movements.item.unit}</b></span>
                    <span className="chip small">Opening: {movements.totals.opening_balance}</span>
                    <span className="chip green small"><ArrowDownCircle size={12} /> In {movements.totals.total_in}</span>
                    <span className="chip amber small"><ArrowUpCircle size={12} /> Out {movements.totals.total_out}</span>
                    <span className="chip small">{movements.totals.count} movement(s)</span>
                  </div>
                )}
                <div className="table-wrap" style={{ maxHeight: 400, overflow: "auto" }}>
                  <table>
                    <thead>
                      <tr><th>Date</th><th>Type</th><th>Ref</th><th>Detail</th><th style={{ textAlign: "right" }}>In</th><th style={{ textAlign: "right" }}>Out</th><th style={{ textAlign: "right" }}>Balance</th></tr>
                    </thead>
                    <tbody>
                      {movements.totals && (
                        <tr style={{ background: "var(--surface-2, #f8fafc)" }}>
                          <td colSpan={6}><em>Opening balance</em></td>
                          <td style={{ textAlign: "right" }}><strong>{movements.totals.opening_balance}</strong></td>
                        </tr>
                      )}
                      {movements.rows.length === 0 && (
                        <tr><td colSpan={7} style={{ textAlign: "center", padding: 30 }}>No recorded movements for this item.</td></tr>
                      )}
                      {movements.rows.map((m, idx) => {
                        const tone = m.type.startsWith("IN") ? "green" : m.type.startsWith("ADJ") ? "blue" : "amber";
                        return (
                          <tr key={idx} data-testid={`movement-row-${idx}`}>
                            <td>{(m.date || "").slice(0, 10)}</td>
                            <td><Badge tone={tone}>{m.type}</Badge></td>
                            <td><code>{m.ref || "-"}</code></td>
                            <td><small>{m.detail || "-"}</small></td>
                            <td style={{ textAlign: "right" }}>{m.qty_in ? m.qty_in : ""}</td>
                            <td style={{ textAlign: "right" }}>{m.qty_out ? m.qty_out : ""}</td>
                            <td style={{ textAlign: "right" }}><strong>{m.balance}</strong></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

function defaultForm() {
  return {
    sku: "",
    name: "",
    category: "Protein",
    unit: "PCS",
    stock: "0",
    min_stock: "10",
    cost: "0",
    outlet_code: "main_wh",
    supplier: "",
    notes: "",
    purchase_uom: "",
    issue_uom: "",
    uom_conversions: [],
    is_direct: true,
    account_code: "",
    rm_stock_code: "",
  };
}
